import { useEffect, useMemo, useState } from "react";

import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import {
  EncounterDetailVersionPair,
  readEncounterHistory,
  resolveEncounterDetailVersionPairForDiff,
} from "@/encounters/utils/encounter-detail-version-diff";
import { getQuestionnaireResponseChangeInstant } from "@/encounters/utils/questionnaire-response-instant";
import { showSystemErrorNotification } from "@/utils/notifications";

export interface EncounterDetailVersionPairState extends EncounterDetailVersionPair {
  /**
   * True while the Encounter history read backing the pair is still in flight. Until it settles we
   * cannot tell "no pair for this amendment" (fall back to the questionnaire answers) from "history
   * not loaded yet", so callers should hold off on the answers fallback while this is true;
   * otherwise the first render shows the answers-based values before the historical pair arrives —
   * a flash of wrong Encounter Information.
   */
  isLoading: boolean;
}

/**
 * Resolves the pair of Encounter versions to diff for the amendment currently being viewed.
 *
 * The Encounter history read runs on its own effect (keyed only on the encounter id) so it does
 * not serialize behind the amendment-history fetch, and the pair is resolved synchronously once
 * both the history and the amendment being viewed are known.
 *
 * While the read is in flight `isLoading` is true and the pair is empty; on failure the history
 * settles to empty so `isLoading` clears and callers fall back to the questionnaire answers rather
 * than hanging on a spinner.
 *
 * @param encounterId Encounter whose version history to read.
 * @param amendmentHistory Amendment (QuestionnaireResponse) history, newest first.
 * @param currentVersionIndex Index into `amendmentHistory` of the amendment being viewed.
 */
export function useEncounterDetailVersionPair(
  encounterId: string | undefined,
  amendmentHistory: QuestionnaireResponse[] | undefined,
  currentVersionIndex: number,
): EncounterDetailVersionPairState {
  const medplum = useMedplum();
  const [encounterHistory, setEncounterHistory] = useState<Encounter[]>();

  useEffect(() => {
    if (!encounterId) return;

    let isCurrent = true;
    // Reset so `isLoading` reflects the new encounter rather than the previous one's history.
    setEncounterHistory(undefined);

    const loadEncounterHistory = async () => {
      try {
        const history = await readEncounterHistory(medplum, encounterId);
        if (isCurrent) setEncounterHistory(history);
      } catch (error) {
        Sentry.captureException(error);
        showSystemErrorNotification("Failed to load encounter history");
        // Settle to empty so the pair resolves to {} and callers fall back to the answers, rather
        // than leaving `isLoading` stuck true forever after a failed read.
        if (isCurrent) setEncounterHistory([]);
      }
    };

    loadEncounterHistory();

    return () => {
      isCurrent = false;
    };
  }, [encounterId, medplum]);

  const isLoading = Boolean(encounterId) && encounterHistory === undefined;

  const pair = useMemo(() => {
    if (!encounterHistory || !amendmentHistory?.length) return {};

    const nextAmendment =
      currentVersionIndex > 0 ? amendmentHistory[currentVersionIndex - 1] : undefined;

    return resolveEncounterDetailVersionPairForDiff(
      encounterHistory,
      getQuestionnaireResponseChangeInstant(amendmentHistory[currentVersionIndex]),
      nextAmendment ? getQuestionnaireResponseChangeInstant(nextAmendment) : undefined,
    );
  }, [encounterHistory, amendmentHistory, currentVersionIndex]);

  return { ...pair, isLoading };
}
